import React, { useState, useRef, useMemo, useCallback } from "react";
import { parseMindMapBlock } from "@/lib/chat/parseMindMapBlock";
import { parseTimelineBlock } from "@/lib/chat/parseTimelineBlock";
import { parseFlowchartBlock } from "@/lib/chat/parseFlowchartBlock";
import { parseBarChartBlock } from "@/lib/chat/parseBarChartBlock";
import { parseLineChartBlock } from "@/lib/chat/parseLineChartBlock";
import { parsePieChartBlock } from "@/lib/chat/parsePieChartBlock";
import { parseVennDiagramBlock } from "@/lib/chat/parseVennDiagramBlock";
import { MindMap } from "@/components/chat/mind-map/MindMap";
import { Timeline } from "@/components/chat/timeline/Timeline";
import { Flowchart } from "@/components/chat/flowchart/Flowchart";
import { BarChart } from "@/components/chat/bar-chart/BarChart";
import { LineChart } from "@/components/chat/line-chart/LineChart";
import { PieChart } from "@/components/chat/pie-chart/PieChart";
import { VennDiagram } from "@/components/chat/venn-diagram/VennDiagram";
import { Copy, ThumbsUp, ThumbsDown, Volume2, Square } from "lucide-react";
import { TypingIndicator } from "./TypingIndicator";
import { EditMessageBubble } from "./EditMessageBubble";
import { SelectedTextFloatingToolbar } from "./SelectedTextFloatingToolbar";
import { CitationBadge } from "./CitationBadge";
import { SourcesFooter } from "./SourcesFooter";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FeedbackPopover } from "@/components/FeedbackPopover";
import { generateFlashcardsWithAI } from "@/features/flashcards/api";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useNotesEditor } from "@/context/NotesEditorContext";
import { $getRoot, $createParagraphNode, $createTextNode } from "lexical";
import { generateQuizFromText } from "@/features/quizzes/generateQuiz";
import { useQueryClient } from "@tanstack/react-query";
import { processCitations, normalizeAnnotations, isCitationLink } from "@/lib/chat/citations";
import type { CitationSource } from "@/lib/chat/citations";
import type { ChatTabId } from "../ChatRoot";

interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  message: string;
  created_at: string;
  metadata?: any;
}

interface ChatMessagesProps {
  messages: Message[];
  isAITyping?: boolean;
  chatId?: string;
  onEditMessage?: (messageId: string, newText: string) => Promise<void>;
  onSendMessage?: (text: string) => void;
  onSetReplyText?: (text: string) => void;
  onSwitchTab?: (tab: ChatTabId) => void;
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({ 
  messages, 
  isAITyping = false, 
  chatId,
  onEditMessage, 
  onSendMessage, 
  onSetReplyText,
  onSwitchTab 
}) => {
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [isGeneratingFlashcards, setIsGeneratingFlashcards] = useState(false);
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
  const [likedMessages, setLikedMessages] = useState<Set<number>>(new Set());
  const aiMessagesRef = useRef<HTMLDivElement>(null);
  
  const { user } = useAuth();
  const { editor: notesEditor } = useNotesEditor();
  const queryClient = useQueryClient();

  const handleCopyMessage = useCallback(async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  }, []);

  const handleLikeMessage = useCallback((index: number) => {
    setLikedMessages(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
        toast.success("Thanks for your feedback");
      }
      return next;
    });
  }, []);

  const handleToolbarAction = async (actionId: string, selectedText: string) => {
    if (actionId === "explain") {
      if (onSendMessage) {
        onSendMessage(`Explain this in concise detail: "${selectedText}"`);
      }
      return;
    }

    if (actionId === "chat") {
      if (onSetReplyText) {
        onSetReplyText(selectedText);
        toast.success("Text quoted. Type your follow-up message below.");
      }
      return;
    }

    if (actionId === "add-to-notes") {
      if (!notesEditor) {
        toast.error("Notes editor not available. Switch to Notes tab first.");
        return;
      }
      notesEditor.update(() => {
        const root = $getRoot();
        const paragraph = $createParagraphNode();
        paragraph.append($createTextNode(selectedText));
        root.append(paragraph);
      });
      toast.success("Added to notes!");
      return;
    }
    
    if (actionId === "flashcards") {
      if (!user) {
        toast.error("Please sign in to create flashcards");
        return;
      }
      if (isGeneratingFlashcards) {
        toast.info("Already generating flashcards...");
        return;
      }
      
      setIsGeneratingFlashcards(true);
      
      toast.promise(
        generateFlashcardsWithAI({
          userId: user.id,
          contentId: null,
          chatId: chatId ?? null,
          topic: null,
          sourceText: selectedText,
          numCards: Math.min(10, Math.max(3, Math.floor(selectedText.length / 200))),
        }).then((result) => {
          // Invalidate flashcard decks query to refresh the list
          queryClient.invalidateQueries({ queryKey: ["flashcard-decks", user.id] });
          // Auto-switch to flashcards tab after generation
          if (onSwitchTab) {
            onSwitchTab("flashcards");
          }
          return result;
        }).finally(() => {
          setIsGeneratingFlashcards(false);
        }),
        {
          loading: "Generating flashcards...",
          success: "Flashcards created!",
          error: (err) => err instanceof Error ? err.message : "Failed to generate flashcards",
        }
      );
      return;
    }

    if (actionId === "quiz") {
      if (!user) {
        toast.error("Please sign in to create quizzes");
        return;
      }
      if (isGeneratingQuiz) {
        toast.info("Already generating a quiz...");
        return;
      }
      
      setIsGeneratingQuiz(true);
      
      toast.promise(
        generateQuizFromText({
          userId: user.id,
          sourceContext: selectedText,
          questionCount: Math.min(5, Math.max(3, Math.floor(selectedText.length / 300))),
          questionTypes: ["multiple_choice", "true_false"],
          difficulty: "medium",
        }).then((result) => {
          // Invalidate quizzes query to refresh the list
          queryClient.invalidateQueries({ queryKey: ["quizzes", user.id] });
          // Switch to quizzes tab
          if (onSwitchTab) {
            onSwitchTab("quizzes");
          }
          return result;
        }).finally(() => {
          setIsGeneratingQuiz(false);
        }),
        {
          loading: "Generating quiz...",
          success: "Quiz created!",
          error: (err) => err instanceof Error ? err.message : "Failed to generate quiz",
        }
      );
      return;
    }

    console.log(`Action: ${actionId}, Text: ${selectedText}`);
    // TODO: Implement other actions (read-aloud)
  };

  const handleStartEdit = (msg: Message) => {
    setEditingMessageId(msg.id);
    setEditText(msg.message);
  };

  const handleSaveEdit = async (messageId: string) => {
    if (!editText.trim() || !onEditMessage) return;
    
    try {
      await onEditMessage(messageId, editText.trim());
      setEditingMessageId(null);
      setEditText("");
    } catch (error) {
      console.error("Failed to edit message:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditText("");
  };
  return (
    <div className="w-full space-y-8 pb-8">
      <SelectedTextFloatingToolbar 
        containerRef={aiMessagesRef} 
        onAction={handleToolbarAction}
      />
      <div ref={aiMessagesRef}>
        {messages.map((msg, idx) => {
          const isUser = msg.role === "user";
          const nextMsg = messages[idx + 1];
          const showActions = !isUser && (!nextMsg || nextMsg.role !== "assistant") && !isAITyping;

          const isEditing = editingMessageId === msg.id;

          return (
            <div key={msg.id} className="space-y-8">
              <div className="flex flex-col gap-2">
                <div className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}>
                  {isUser ? (
                    isEditing ? (
                      <EditMessageBubble
                        text={editText}
                        onTextChange={setEditText}
                        onSave={() => handleSaveEdit(msg.id)}
                        onCancel={handleCancelEdit}
                      />
                    ) : (
                      <UserMessageBubble 
                        text={msg.message} 
                        onEdit={() => handleStartEdit(msg)}
                      />
                    )
                  ) : (
                    <div className="overflow-x-auto w-full">
                      <div className="flex w-full flex-col">
                        <div className="w-full">
                          <div className="flex w-full justify-start">
                            <AIMessageWithCitations message={msg} />
                          </div>
                        </div>
                        {showActions && (
                          <MessageActionsRow 
                            content={msg.message}
                            index={idx}
                            isLiked={likedMessages.has(idx)}
                            onCopy={handleCopyMessage}
                            onLike={handleLikeMessage}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Typing Indicator */}
      {isAITyping && (
        <div className="space-y-8 animate-fade-in">
          <div className="flex flex-col gap-2">
            <TypingIndicator />
          </div>
        </div>
      )}
    </div>
  );
};

interface UserMessageBubbleProps {
  text: string;
  onEdit?: () => void;
}

const UserMessageBubble: React.FC<UserMessageBubbleProps> = ({ text, onEdit }) => {
  return (
    <div className="group/message flex w-full flex-col items-end gap-1">
      <div className="flex w-full justify-end">
        <div className="relative w-fit rounded-3xl border border-primary/10 bg-primary/5 p-3 text-left leading-relaxed text-foreground">
          <p className="text-base leading-7 last:mb-0 whitespace-pre-wrap">
            {text}
          </p>
        </div>
      </div>
      {/* edit icon on hover */}
      <div className="flex items-center gap-1 opacity-100 transition-opacity duration-200 lg:opacity-0 lg:group-hover/message:opacity-100">
        <button
          onClick={onEdit}
          className="rounded-full p-1 transition-colors hover:bg-primary/10"
          aria-label="Edit message"
        >
          <svg
            className="h-3 w-3 text-primary/60"
            viewBox="0 0 24 24"
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Markdown components shared across message rendering
const markdownComponents = (sources: CitationSource[]) => ({
  p: ({ children }: any) => <p className="mb-4 last:mb-0">{children}</p>,
  ul: ({ children }: any) => <ul className="mb-4 ml-6 list-disc space-y-2">{children}</ul>,
  ol: ({ children }: any) => <ol className="mb-4 ml-6 list-decimal space-y-2">{children}</ol>,
  li: ({ children }: any) => <li className="leading-7">{children}</li>,
  strong: ({ children }: any) => <strong className="font-semibold">{children}</strong>,
  h1: ({ children }: any) => <h1 className="mb-4 mt-6 text-2xl font-bold first:mt-0">{children}</h1>,
  h2: ({ children }: any) => <h2 className="mb-3 mt-5 text-xl font-semibold first:mt-0">{children}</h2>,
  h3: ({ children }: any) => <h3 className="mb-2 mt-4 text-lg font-semibold first:mt-0">{children}</h3>,
  code: ({ className, children, ...props }: any) => {
    const isInline = !className;

    // Intercept mindmap code blocks
    if (className === "language-mindmap") {
      const raw = String(children).replace(/\n$/, "");
      const parsed = parseMindMapBlock(raw);
      if (parsed) {
        return <MindMap title={parsed.title} data={parsed.data} description={parsed.description} />;
      }
    }

    // Intercept timeline code blocks
    if (className === "language-timeline") {
      const raw = String(children).replace(/\n$/, "");
      const parsed = parseTimelineBlock(raw);
      if (parsed) {
        return <Timeline data={parsed} />;
      }
    }

    // Intercept flowchart code blocks
    if (className === "language-flowchart") {
      const raw = String(children).replace(/\n$/, "");
      const parsed = parseFlowchartBlock(raw);
      if (parsed) {
        return <Flowchart data={parsed} />;
      }
    }

    // Intercept barchart code blocks
    if (className === "language-barchart") {
      const raw = String(children).replace(/\n$/, "");
      const parsed = parseBarChartBlock(raw);
      if (parsed) {
        return <BarChart data={parsed} />;
      }
    }

    // Intercept linechart code blocks
    if (className === "language-linechart") {
      const raw = String(children).replace(/\n$/, "");
      const parsed = parseLineChartBlock(raw);
      if (parsed) {
        return <LineChart data={parsed} />;
      }
    }

    // Intercept piechart code blocks
    if (className === "language-piechart") {
      const raw = String(children).replace(/\n$/, "");
      const parsed = parsePieChartBlock(raw);
      if (parsed) {
        return <PieChart data={parsed} />;
      }
    }

    // Intercept venndiagram code blocks
    if (className === "language-venndiagram") {
      const raw = String(children).replace(/\n$/, "");
      const parsed = parseVennDiagramBlock(raw);
      if (parsed) {
        return <VennDiagram data={parsed} />;
      }
    }

    return isInline ? (
      <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono" {...props}>{children}</code>
    ) : (
      <code className={`block rounded-lg bg-muted p-4 text-sm font-mono overflow-x-auto ${className}`} {...props}>{children}</code>
    );
  },
  pre: ({ children }: any) => {
    // If the child is a Timeline or MindMap (not a <code> element), render without <pre> wrapper
    const child = React.Children.toArray(children)[0] as any;
    if (child && child.type && typeof child.type !== 'string') {
      return <div className="mb-4">{children}</div>;
    }
    return <pre className="mb-4 overflow-x-auto">{children}</pre>;
  },
  blockquote: ({ children }: any) => <blockquote className="border-l-4 border-primary/30 pl-4 italic my-4">{children}</blockquote>,
  a: ({ href, children, ...props }: any) => {
    const citNum = isCitationLink(children);
    if (citNum !== null && sources.length > 0) {
      const source = sources.find(s => s.index === citNum);
      if (source) {
        return <CitationBadge index={citNum} source={source} />;
      }
    }
    return <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline" {...props}>{children}</a>;
  },
});

const AIMessageWithCitations: React.FC<{ message: Message }> = ({ message }) => {
  const annotations = message.metadata?.annotations;
  const normalized = useMemo(() => normalizeAnnotations(annotations || []), [annotations]);
  const processed = useMemo(() => processCitations(message.message, normalized), [message.message, normalized]);

  return (
    <div className="w-full" style={{ position: "relative" }}>
      <div className="group relative w-full rounded-3xl bg-transparent p-0 pt-1 text-left leading-relaxed text-foreground">
        <div className="prose prose-neutral max-w-none dark:prose-invert">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents(processed.sources)}
          >
            {processed.text}
          </ReactMarkdown>
          <SourcesFooter sources={processed.sources} />
        </div>
      </div>
    </div>
  );
};

interface MessageActionsRowProps {
  content: string;
  index: number;
  isLiked: boolean;
  onCopy: (content: string) => void;
  onLike: (index: number) => void;
}

const MessageActionsRow: React.FC<MessageActionsRowProps> = ({ content, index, isLiked, onCopy, onLike }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleReadAloud = useCallback(async () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      return;
    }

    setIsLoading(true);
    try {
      const { playAIResponse } = await import("@/lib/audio/playAIResponse");
      await playAIResponse(content, audioRef, setIsPlaying);
    } catch (error) {
      console.error("TTS playback failed:", error);
      const { toast } = await import("sonner");
      toast.error("Failed to play audio");
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  }, [content, isPlaying]);

  return (
    <div className="py-3">
      <div className="flex flex-row items-center justify-between space-x-1">
        <div className="flex flex-row items-center space-x-1">
          <IconButton 
            icon={<Copy className="h-4 w-4" />} 
            onClick={() => onCopy(content)}
            ariaLabel="Copy message"
          />
          <IconButton 
            icon={<ThumbsUp className="h-4 w-4" fill={isLiked ? "currentColor" : "none"} />} 
            onClick={() => onLike(index)}
            ariaLabel="Like response"
          />
          <FeedbackPopover>
            <IconButton 
              icon={<ThumbsDown className="h-4 w-4" />} 
              ariaLabel="Dislike response"
            />
          </FeedbackPopover>
          <IconButton 
            icon={
              isPlaying 
                ? <Square className="h-4 w-4" fill="currentColor" /> 
                : <Volume2 className="h-4 w-4" />
            }
            onClick={handleReadAloud}
            ariaLabel={isPlaying ? "Stop reading" : "Read aloud"}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

const IconButton = React.forwardRef<HTMLButtonElement, { icon: React.ReactNode; onClick?: () => void; ariaLabel?: string; disabled?: boolean }>(
  ({ icon, onClick, ariaLabel, disabled, ...props }, ref) => (
    <button 
      ref={ref}
      onClick={onClick}
      aria-label={ariaLabel}
      disabled={disabled}
      className="inline-flex h-7 w-7 items-center justify-center rounded-lg p-1.5 text-sm font-medium text-muted-foreground ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
      {...props}
    >
      {icon}
    </button>
  )
);
IconButton.displayName = "IconButton";
